import { getPropertyNames } from 'utils/object';

export type ObjectConstraints<TObject extends Record<string, unknown>> = {
    [key in keyof TObject]-?: (value: unknown) => boolean;
};

export const isObject = (value: unknown) => typeof value === 'object';
export const isBoolean = (value: unknown): value is boolean => typeof value === 'boolean';
export const isString = (value: unknown): value is string => typeof value === 'string';

export const isNumber = (value: unknown): value is number =>
    typeof value === 'number' && !Number.isNaN(value);

export const isNumberInRangeInclusive = (
    value: unknown,
    minValue: number,
    maxValue: number
): value is number => isNumber(value) && value >= minValue && value <= maxValue;

export const isDate = (value: unknown): value is Date => value instanceof Date;

export const isArray = (
    value: unknown,
    itemValidator?: (item: unknown) => boolean
): value is unknown[] =>
    Array.isArray(value) && (!itemValidator || (value as unknown[]).every(itemValidator));

export const valueInList = <TItem>(
    value: unknown,
    validValues: TItem[]
): value is TItem => (validValues as unknown[]).includes(value);

export function isObjectValid<TObject extends Record<string, unknown>>(
    obj: unknown,
    constraints: ObjectConstraints<TObject>
): obj is TObject {
    if (!isObject(obj) || Array.isArray(obj)) {
        return false;
    }

    // Essentially saying "This is indexable with `keyof TObject`" so that the
    // below checks don't scream. This is broadly true, as `{}` can be indexed
    // by any string, will just give `undefined` if not exists.
    const objA = obj as { [K in keyof TObject]?: unknown };

    return getPropertyNames(constraints).every((key) => constraints[key](objA[key]));
}

export function isPartialObjectValid<TObject extends Record<string, unknown>>(
    obj: unknown,
    constraints: ObjectConstraints<TObject>
): obj is Partial<TObject> {
    if (!isObject(obj) || Array.isArray(obj) || obj === null) {
        return false;
    }

    // Essentially saying "This is indexable with `keyof TObject`" so that the
    // below checks don't scream. This is broadly true, as `{}` can be indexed
    // by any string, will just give `undefined` if not exists.
    const objA = obj as { [K in keyof TObject]?: unknown };

    return getPropertyNames(constraints).every(
        (key) => !(key in objA) || objA[key] === undefined || constraints[key](objA[key])
    );
}
